import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AbilityLimitation,
  ARMOR_TYPE_OPTIONS,
  AttachedTable,
  Modifier,
  SKILL_OPTIONS,
  SpellLimitation,
  UpgradableNumber,
  WeaponLimitation
} from "@dndModels";
import { 
  // DAMAGE_TYPES, 
  // SCHOOLS,
  // ATTACK_TYPES,
  ABILITY_SCORES_EXPANDED,
  ABILITY_SCORES,
  MODIFIER_TARGETS
} from "@dndModels/Constants";
import { DataType, ModifierTarget, ModType } from "@dndModels/Types";

// import DataUtilities from "../../../../utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import SelectTableBox from "@dndComponents/select/SelectTableBox";
import WeaponLimitInput from "@dndComponents/limitations/WeaponLimitInput";
import AbilityLimitInput from "@dndComponents/limitations/AbilityLimitInput";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";

interface Props { 
  obj: Modifier;
  disabled?: boolean;
  attached_table?: AttachedTable;
  onChange: (changed: Modifier) => void; 
}

// const common_util = DataUtilities.getInstance();

export default function ModifierInput({
  obj,
  disabled = false,
  attached_table,
  onChange,
}: Props) {
  // const [fetched, setFetched] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [basic, setBasic] = useState(obj.rolls.flat.isBasic() && obj.rolls.count.isBasic() && obj.rolls.size.isBasic());


  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderDetails = () => {
    return (
      <>
        { renderSubDetails() }
        { renderArmorRequirements() }
        <Grid
          size={{
            xs: 12
          }}>
          { renderModTypeSelect() }
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { obj.mod_type === "Add Bonus Rolls" ? renderRolls() : renderAmountUNB() }
        </Grid>
      </>
    );
  }

  const renderSubDetails = () =>{
    let return_me: any = null;
    switch (obj.modifies) { 
      case "Speed": 
        return_me =
          <SelectStringBox 
            name="Speed Types" 
            options={["Walking","Swimming","Flying","Climbing","Burrowing"]}
            multiple
            values={obj.modifies_details} 
            disabled={disabled}
            onChange={(values: string[]) => {
              const new_obj = obj;
              new_obj.modifies_details = values;
              onChange(new_obj);
            }} 
          />;
      break;
      case "Saving Throw":
        return_me =
          <SelectStringBox 
            name="Ability Score" 
            options={ABILITY_SCORES_EXPANDED}
            extra_options={["Death Saves"]}
            multiple
            values={obj.modifies_details} 
            disabled={disabled}
            onChange={(values: string[]) => {
              const new_obj = obj;
              new_obj.modifies_details = values;
              onChange(new_obj);
            }} 
          />;
      break;
      case "Ability Score":
      case "Ability Score Max":
      case "Ability Check":
        return_me =
          <SelectStringBox 
            name="Ability Score" 
            options={ABILITY_SCORES_EXPANDED}
            multiple
            values={obj.modifies_details} 
            disabled={disabled}
            onChange={(values: string[]) => {
              const new_obj = obj;
              new_obj.modifies_details = values;
              onChange(new_obj);
            }} 
          />;
      break;
      case "Skill Check":
        return_me =
          <SelectStringBox 
            name="Skills" 
            options={SKILL_OPTIONS}
            multiple
            values={obj.modifies_details} 
            disabled={disabled}
            onChange={(values: string[]) => {
              const new_obj = obj;
              new_obj.modifies_details = values;
              onChange(new_obj);
            }} 
          />;
      break;
      case "Spell DC": 
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 6
              }}>{ renderClassSelect() }</Grid>
            <Grid
              size={{
                xs: 6
              }}>{ renderSCAbilitySelect() }</Grid>
          </Grid>
        );
      break;
      case "Max Prepared Spells":
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>{ renderClassSelect() }</Grid>
          </Grid>
        );
      break;
      case "Attack":
      case "Damage":
      case "Range":
        return_me = (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 3
              }}>
              <SelectStringBox 
                name="Applies To"
                options={["Weapons","Abilities","Spells"]}
                values={obj.apply_to}
                multiple
                onChange={(changed: ("Weapons" | "Abilities" | "Spells")[]) => {
                  const new_obj = obj;
                  new_obj.apply_to = changed;
                  onChange(new_obj);
                }}
              />
            </Grid>
            { obj.apply_to.includes("Weapons") && 
              <Grid
                size={{
                  xs: 12
                }}>
                <WeaponLimitInput
                  obj={obj.weapon_limit}
                  disabled={disabled}
                  onChange={(changed: WeaponLimitation) => {
                    const new_obj = obj;
                    new_obj.weapon_limit = changed.clone();
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
            { obj.apply_to.includes("Abilities") && 
              <Grid
                size={{
                  xs: 12
                }}>
                <AbilityLimitInput
                  obj={obj.ability_limit}
                  disabled={disabled}
                  onChange={(changed: AbilityLimitation) => {
                    const new_obj = obj;
                    new_obj.ability_limit = changed.clone();
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
            { obj.apply_to.includes("Spells") && 
              <Grid
                size={{
                  xs: 12
                }}>
                <SpellLimitInput
                  obj={obj.spell_limit}
                  disabled={disabled}
                  onChange={(changed: SpellLimitation) => {
                    const new_obj = obj;
                    new_obj.spell_limit = changed.clone();
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
          </Grid>
        );
        // return_me =
        //   <Grid container spacing={0} direction="row">
        //     <Grid item xs={6}>
        //       { renderAttackTypeSelect() }
        //     </Grid>
        //     <Grid item xs={ obj.the_damage_types.includes("Selectable") ? 3 : 6 }>
        //       { renderDamageTypeSelect() }
        //     </Grid>
        //     { obj.the_damage_types.includes("Selectable") && 
        //       <Grid item xs={3}>
        //         <StringBox
        //           name="Selectable Key"
        //           value={obj.damage_selectable_key}
        //           onBlur={(changed: string) => {
        //             const new_obj = obj;
        //             new_obj.damage_selectable_key = changed;
        //             onChange(new_obj);
        //           }}
        //         />
        //       </Grid>
        //     }
        //     { (obj.modifies_details.length === 0 || 
        //       obj.modifies_details.includes("Melee Spell") || 
        //       obj.modifies_details.includes("Ranged Spell") || 
        //       obj.modifies_details.includes("Spell Attacks") || 
        //       obj.modifies_details.includes("Cantrip Attacks") || 
        //       obj.modifies_details.includes("Save")) &&
        //       <>
        //         <Grid item xs={4}>
        //           { renderClassSelect() }
        //         </Grid>
        //         <Grid item xs={4}>
        //           { renderSCAbilitySelect() }
        //         </Grid>
        //         <Grid item xs={4}>
        //           { renderSpellListSelect() }
        //         </Grid>
        //         <Grid item xs={6}>
        //           { renderMagicSchoolSelect() }
        //         </Grid>
        //         <Grid item xs={6}>
        //           { renderSpellLevelSelect() }
        //         </Grid>
        //       </>
        //     }
        //     { common_util.any_overlaps(obj.modifies_details, ["Melee","Ranged","Thrown",...ABILITY_SCORES_EXPANDED]) &&
        //       <>
        //         <Grid item xs={6}>
        //           <SelectObjectBox 
        //             name="Weapon Keyword" 
        //             system="dandd"
        //             data_type="weapon_keyword"
        //             extra_options={["Any","Any Weapon","Unarmed Strike"]}
        //             multiple
        //             values={obj.weapon_keywords} 
        //             disabled={disabled}
        //             onChange={(values: string[]) => {
        //               const new_obj = obj;
        //               new_obj.weapon_keywords = values;
        //               onChange(new_obj);
        //             }} 
        //           />
        //         </Grid>
        //         <Grid item xs={6}>
        //           <SelectObjectBox 
        //             name="Excluded Weapon Keywords" 
        //             system="dandd"
        //             data_type="weapon_keyword"
        //             multiple
        //             values={obj.excluded_weapon_keywords} 
        //             disabled={disabled}
        //             onChange={(values: string[]) => {
        //               const new_obj = obj;
        //               new_obj.excluded_weapon_keywords = values;
        //               onChange(new_obj);
        //             }} 
        //           />
        //         </Grid>
        //       </>
        //     }
        //   </Grid>;
      break;
      case "Table Value": return_me = renderTableValueSelect(); break;
    }
    console.log(obj);
    if (return_me) {
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            { return_me }
          </Grid>
          { obj.modifies_details.includes("Selectable") &&
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Selectable Key"
                value={obj.selectable_key}
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.selectable_key = changed;
                  onChange(new_obj);
                }}
              />
            </Grid>
          }
        </>
      );
    }
    return null;
  }

  // const renderAttackTypeSelect = () =>{
  //   return (
  //     <SelectStringBox 
  //       name="Attack Types" 
  //       options={[...ATTACK_TYPES,...ABILITY_SCORES_EXPANDED,"Spell Attacks","Cantrip Attacks"]}
  //       multiple
  //       values={obj.modifies_details} 
  //       disabled={disabled}
  //       onChange={(values: string[]) => {
  //         const new_obj = obj;
  //         new_obj.modifies_details = values;
  //         onChange(new_obj);
  //       }} 
  //     />
  //   );
  // }

  // const renderDamageTypeSelect = () =>{
  //   return (
  //     <SelectStringBox 
  //       name="Damage Types" 
  //       options={DAMAGE_TYPES}
  //       extra_options={["Any","Healing","Temp HP","Selectable"]}
  //       auto_sort
  //       multiple
  //       values={obj.the_damage_types} 
  //       disabled={disabled}
  //       onChange={(values: string[]) => {
  //         const new_obj = obj;
  //         new_obj.the_damage_types = values;
  //         onChange(new_obj);
  //       }} 
  //     />
  //   );
  // }

  // const renderMagicSchoolSelect = () =>{
  //   return (
  //     <SelectStringBox 
  //       name="Magic Schools" 
  //       options={SCHOOLS}
  //       extra_options={["Any"]}
  //       auto_sort
  //       multiple
  //       values={obj.magic_schools} 
  //       disabled={disabled}
  //       onChange={(values: string[]) => {
  //         const new_obj = obj;
  //         new_obj.magic_schools = values;
  //         onChange(new_obj);
  //       }} 
  //     />
  //   );
  // }

  // const renderSpellLevelSelect = () =>{
  //   return (
  //     <SelectStringBox 
  //       name="Spell Slot Levels" 
  //       options={["Any","Cantrip","1","2","3","4","5","6","7","8","9"]}
  //       multiple
  //       values={obj.slot_levels} 
  //       disabled={disabled}
  //       onChange={(values: string[]) => {
  //         const new_obj = obj;
  //         new_obj.slot_levels = values;
  //         onChange(new_obj);
  //       }} 
  //     />
  //   );
  // }

  const renderClassSelect = () =>{
    return (
      <SelectObjectBox 
        name="For Class" 
        data_type="game_class"
        extra_options={["Parent"]}
        multiple
        values={obj.class_ids} 
        disabled={disabled}
        onChange={(changed: string[]) => {
          const new_obj = obj;
          new_obj.class_ids = changed;
          if (changed.length > 0) {
            new_obj.ability_scores = [];
          }
          onChange(new_obj);
        }} 
      />
    );
  }

  const renderSCAbilitySelect = () =>{
    return (
      <SelectStringBox 
        name="For Spellcasting Ability" 
        options={ABILITY_SCORES}
        multiple
        values={obj.ability_scores} 
        disabled={disabled || obj.class_ids.length > 0}
        onChange={(changed: string[]) => {
          const new_obj = obj;
          new_obj.ability_scores = changed;
          onChange(new_obj);
        }} 
      />
    );
  }

  // const renderSpellListSelect = () =>{
  //   return (
  //     <SelectObjectBox 
  //       name="Spell List" 
  //       system="dandd"
  //       data_type="spell_list"
  //       multiple
  //       values={obj.spell_list_ids} 
  //       disabled={disabled}
  //       onChange={(values: string[]) => {
  //         const new_obj = obj;
  //         new_obj.spell_list_ids = values;
  //         onChange(new_obj);
  //       }} 
  //     />
  //   );
  // }

  const renderAmountUNB = () =>{
    return (
      <UpgradableNumberBox 
        name="Amount"
        value={obj.amount_unb} 
        disabled={disabled}
        attached_table={attached_table}
        onChange={(value: UpgradableNumber) => {
          const new_obj = obj;
          new_obj.amount_unb = value;
          onChange(new_obj);
        }}
      />
    );
  }

  const renderModTypeSelect = () =>{
    let mod_types = ["Add","Add if > 0","Multiply","Multiply if > 0","Replace","Replace if Greater"];
    if (["Attack","Damage","Saving Throw",
      "Ability Check","Skill Check","Initiative"].includes(obj.modifies)) {
      mod_types.push("Add Bonus Rolls");
    } else if (["Ability Score Max","Ability Score"].includes(obj.modifies)) {
      mod_types = ["Add","Add if > 0","Replace","Replace if Greater"];
    }
    return (
      <SelectStringBox 
        name="Modification Type" 
        options={mod_types}
        value={obj.mod_type} 
        disabled={disabled}
        onChange={(changed: ModType) => {
          const new_obj = obj;
          new_obj.mod_type = changed;
          onChange(new_obj);
        }} 
      />
    );
  }

  const renderArmorRequirements = () =>{
    if (!["Max HP","Spell DC"].includes(obj.modifies)) {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox 
              name="Allowed Armor Types" 
              options={ARMOR_TYPE_OPTIONS}
              values={obj.allowed_armor_types} 
              multiple
              extra_options={["Any","None"]}
              disabled={disabled}
              onChange={(values: string[]) => {
                const new_obj = obj;
                if (values.includes("None")) {
                  if (new_obj.allowed_armor_types.includes("None") && values.length > 0) {
                    values = values.filter(o => o !== "None");
                    new_obj.allowed_armor_types = values;
                  } else {
                    new_obj.allowed_armor_types = ["None"];
                    new_obj.required_armor_types = [];
                  }
                } else {
                  new_obj.allowed_armor_types = values;
                }
                onChange(new_obj);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox 
              name="Required Armor Types" 
              options={ARMOR_TYPE_OPTIONS}
              extra_options={["None"]}
              values={obj.required_armor_types} 
              multiple
              disabled={disabled || obj.allowed_armor_types.includes("None")}
              onChange={(values: string[]) => {
                const new_obj = obj;
                new_obj.required_armor_types = values;
                onChange(new_obj);
              }} 
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  const renderTableValueSelect = () =>{
    return (
      // <Grid container spacing={0} direction="row">
      //   <Grid item xs={4}>
      //     <SelectStringBox
      //       name="Table Type"
      //       value={ obj.modifies_details[0] }
      //       option_map={{
      //         "Class": "game_class",
      //         "Subclass": "subclass",
      //         "Race": "race",
      //         "Subrace": "subrace",
      //         "Gift": "gift"
      //       }}
      //       disabled={disabled}
      //       onChange={(changed: string) => {
      //         const new_obj = obj;
      //         new_obj.modifies_details[0] = changed;
      //         onChange(new_obj);
      //         setReloading(true);
      //       }}
      //     />
      //   </Grid>
      //   <Grid item xs={4}>
      //     { !reloading && obj.modifies_details[0] !== "" &&
      //       <SelectObjectBox
      //         name="Table"
      //         system="dandd"
      //         data_type="table"
      //         filter={{ type: obj.modifies_details[0] }}
      //         value={obj.modifies_details[1]}
      //         disabled={disabled}
      //         onChangeObject={(changed: ModelBase) => {
      //           debugger;
      //           if (changed instanceof Table) {
      //             const new_obj = obj;
      //             new_obj.modifies_details[1] = changed.for_id;
      //             onChange(new_obj);
      //             setTable(changed);
      //             setReloading(true);
      //           }
      //         }}
      //       />
      //     }
      //   </Grid>
      //   <Grid item xs={4}>
      //     { !reloading && table && 
      //       <SelectStringBox
      //         name="Table Field"
      //         value={obj.modifies_details[2]}
      //         options={ table.fields }
      //         auto_sort
      //         disabled={disabled}
      //         onChange={(changed: string) => {
      //           const new_obj = obj;
      //           new_obj.modifies_details[2] = changed;
      //           onChange(new_obj);
      //         }}
      //       />
      //     }
      //   </Grid>
      // </Grid>
      <SelectTableBox
        type={obj.modifies_details[0] as DataType}
        id={obj.modifies_details[1]}
        field={obj.modifies_details[2]}
        onChange={(changed: { type: string, id: string, field: string }) => {
          const new_obj = obj;
          new_obj.modifies_details[0] = changed.type;
          new_obj.modifies_details[1] = changed.id;
          new_obj.modifies_details[2] = changed.field;
          onChange(new_obj);
        }}
      />
    );
  }

  const renderRolls = () =>{
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 3
          }}>
          <ToggleButtonBox 
            name="Basic"
            value={basic} 
            disabled={disabled}
            onToggle={() => {
              const new_basic = !basic;
              if (new_basic) {
                const new_obj = obj;
                new_obj.rolls.flat.makeBasic();
                new_obj.rolls.count.makeBasic();
                new_obj.rolls.size.makeBasic();
                onChange(new_obj);
              }
              setBasic(new_basic);
            }}
          />
        </Grid>
        { basic ? 
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Dice Count"
              value={`${obj.rolls.count.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.rolls.count.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Dice Count"
              value={obj.rolls.count} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.rolls.count = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
        { basic ? 
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Dice Size"
              value={`${obj.rolls.size.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.rolls.size.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Dice Size"
              value={obj.rolls.size} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.rolls.size = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
        { basic ? 
          <Grid
            size={{
              xs: 3
            }}>
            <StringBox 
              name="Flat"
              value={`${obj.rolls.flat.base}`} 
              disabled={disabled}
              type="number"
              onChange={(value: string) => {
                const new_obj = obj;
                new_obj.rolls.flat.base = +value;
                onChange(new_obj);
              }}
            />
          </Grid>
        :
          <Grid
            size={{
              xs: 3
            }}>
            <UpgradableNumberBox 
              name="Flat"
              value={obj.rolls.flat} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(value: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.rolls.flat = value;
                onChange(new_obj);
              }}
            />
          </Grid>
        }
      </Grid>
    );
  }
  
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          name="Modifies" 
          options={MODIFIER_TARGETS}
          auto_sort
          value={obj.modifies} 
          disabled={disabled}
          onChange={(value: ModifierTarget) => {
            const new_obj = obj;
            new_obj.modifies = value;
            if (["Table Value"].includes(value)) {
              new_obj.modifies_details = ["","",""]; // table_type, table_id, table_field
            } else if (["Formula Value"].includes(value)) {
              new_obj.modifies_details = [""];
            } else {
              new_obj.modifies_details = [];
            }
            if (value === "Spell DC") {
              new_obj.modifies_detail_2 = "ALL";
            }
            onChange(new_obj);
          }} 
        />
      </Grid>
      { renderDetails() }
    </Grid>
  );
}